/**
 * AI:
 * Status: 'in progress'
 * Type: Logic
 * SubType: Pure display readers
 * Reason: The pure cell logic of the overview table „Abrechnungskonten" (TM-1000002030, §5.5): one table row
 *         = one slice `bm.CustomerBillingAccountBank` of the opened `cr.CustomerBankAccount`. Billing account
 *         label, slice end („unbefristet"), slice state (Aktuell / Geplant ab ... / Vergangen) with its colour
 *         tokens, and the id of the billing account behind a row.
 *
 *         NOTHING is re-implemented (AU-5): a row is folded through the existing `crReadBankAccountUsage`
 *         (state rule `bmClassifyBankSlice` E-2, open end 9999-12-31 `bmBankSliceEndDateForDisplay` E-7) and
 *         the day format is `crMergeFormatDay`. The only new texts are the three state words and the colour
 *         tokens (presentation). Enum texts (Zahlungsart) are NOT handled here, they come from the backend
 *         EnumInfo (FD-5). No Angular, no service.
 *
 * @ticket TM-1000002030
 */
import {crReadBankAccountUsage} from './cr-bank-account-usage-ql';
import type {CrBankAccountUsageRow} from './cr-bank-account-usage-row';
import {crMergeFormatDay} from './cr-billing-account-merge-display';

/** What the end column prints for an open-ended slice (E-7: 9999-12-31 or no end). */
export const CR_BANK_ACCOUNT_SLICE_OPEN_END_TEXT = 'unbefristet';

/** Style tokens of the state tag. PrimeFlex tokens only, never `[severity]` (global `.p-tag` override). */
const NEUTRAL_STATE_STYLE_CLASS = 'bg-gray-100 text-gray-700';

/**
 * The QL row behind a table cell or a selection event, resolved in this order:
 * `object.row.dto`, `object.row`, `object.dto`, `object` (same resolution as `rowValue` of the dunning block
 * workbench). `null` when nothing usable is passed.
 *
 * @ticket TM-1000002030
 */
export function crBankAccountSliceSourceRow(object: unknown): Record<string, any> | null {
    const candidate: any = object as any;
    const row = candidate?.row?.dto ?? candidate?.row ?? candidate?.dto ?? candidate ?? null;
    return row != null && typeof row === 'object' ? row : null;
}

/**
 * The usage view of one QL row, classified against `todayIso` - delegated to `crReadBankAccountUsage`.
 * `null` when the row is unusable or carries no slice id.
 *
 * @ticket TM-1000002030
 */
export function crBankAccountSliceView(row: Record<string, any> | null | undefined,
                                       todayIso: string): CrBankAccountUsageRow | null {
    if (row == null) {
        return null;
    }
    return crReadBankAccountUsage([row], todayIso)[0] ?? null;
}

/**
 * Billing account label: its name, otherwise „Konto #id", otherwise `–`. The „(geschlossen)" marker is a
 * separate question, see {@link crBankAccountSliceAccountClosed}.
 *
 * @ticket TM-1000002030
 */
export function crBankAccountSliceAccountLabel(view: CrBankAccountUsageRow | null | undefined): string {
    if (view == null) {
        return '–';
    }
    const name: string = (view.accountName ?? '').trim();
    if (name !== '') {
        return name;
    }
    return view.accountId != null ? 'Konto #' + view.accountId : '–';
}

/**
 * Whether the billing account is closed (the cell adds „(geschlossen)" in `text-500`).
 *
 * @ticket TM-1000002030
 */
export function crBankAccountSliceAccountClosed(view: CrBankAccountUsageRow | null | undefined): boolean {
    return view?.closed === true;
}

/**
 * Slice end for display: `dd.MM.yyyy`, or „unbefristet" for 9999-12-31 / no end.
 *
 * @ticket TM-1000002030
 */
export function crBankAccountSliceEndLabel(view: CrBankAccountUsageRow | null | undefined): string {
    if (view == null || view.endDateForDisplay == null) {
        return CR_BANK_ACCOUNT_SLICE_OPEN_END_TEXT;
    }
    return crMergeFormatDay(view.endDateForDisplay);
}

/**
 * State text as of the key date the view was classified against: „Aktuell", „Geplant ab dd.MM.yyyy"
 * or „Vergangen".
 *
 * @ticket TM-1000002030
 */
export function crBankAccountSliceStateLabel(view: CrBankAccountUsageRow | null | undefined): string {
    switch (view?.state) {
        case 'current':
            return 'Aktuell';
        case 'planned':
            return 'Geplant ab ' + crMergeFormatDay(view.startDate);
        case 'ended':
            return 'Vergangen';
        default:
            return '';
    }
}

/**
 * PrimeFlex colour tokens of the state tag; a neutral fallback for an unknown or missing state.
 *
 * @ticket TM-1000002030
 */
export function crBankAccountSliceStateStyleClass(state: string | null | undefined): string {
    switch (state) {
        case 'current':
            return 'bg-green-100 text-green-800';
        case 'planned':
            return 'bg-blue-100 text-blue-800';
        case 'ended':
            return 'bg-gray-200 text-gray-700';
        default:
            return NEUTRAL_STATE_STYLE_CLASS;
    }
}

/**
 * Id of the billing account behind a row: `e.customerBillingAccountId`, else `ba.id`. QL longs may arrive as
 * strings, hence `Number(...)`. `null` when absent or not a number.
 *
 * @ticket TM-1000002030
 */
export function crBankAccountSliceBillingAccountId(row: Record<string, any> | null | undefined): number | null {
    const raw: any = row?.['e.customerBillingAccountId'] ?? row?.['ba.id'] ?? null;
    if (raw == null || raw === '') {
        return null;
    }
    const id: number = Number(raw);
    return Number.isFinite(id) ? id : null;
}
